import { boolean, index, integer, jsonb, pgTable, text, timestamp, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/**
 * Search scenarios — the geography + work-authorization "variable" that
 * makes the platform run parallel job searches. Each scenario reframes the
 * fit-scoring rubric: an "Australia move" scenario needs visa sponsorship and
 * targets Brisbane/Sydney; a "Stateside" scenario needs no visa and targets
 * South Florida + Remote US. A job can be scored under multiple scenarios
 * (one job_scores row per scenario).
 */
export const searchScenarios = pgTable(
  'search_scenarios',
  {
    id: id(),
    userId: text('user_id').notNull(),
    name: varchar('name', { length: 120 }).notNull(),
    active: boolean('active').default(true).notNull(),
    /** The scenario new ingests default to when none is specified. Exactly one
     *  per user should be primary (enforced in the route, not the DB). */
    isPrimary: boolean('is_primary').default(false).notNull(),
    /** Location tokens used both for the rubric framing and for deciding
     *  whether a job is compatible with this scenario before scoring it — now a
     *  DERIVED label view off location_geo_ids.
     *  e.g. ["Brisbane","Sydney","Remote AU"] or ["Miami","Fort Lauderdale","Remote US"]. */
    locations: jsonb('locations').$type<string[]>().notNull(),
    /** Structured scenario geo axis — GeoToken slug ids (geo.ts), the
     *  authoritative location state. countryOf the first that resolves feeds
     *  inferTargetCountry; `locations` is the derived label view. Additive,
     *  defaulted []. */
    locationGeoIds: jsonb('location_geo_ids').$type<string[]>().default([]).notNull(),
    /** au_needs_sponsorship | au_partner_visa | us_authorized | other */
    workAuthorization: varchar('work_authorization', { length: 32 }).notNull(),
    /** Whether a job marked remote counts as compatible with this scenario. */
    remoteOk: boolean('remote_ok').default(true).notNull(),
    targetCompanies: jsonb('target_companies').$type<string[]>(),
    /** Freeform framing injected into the scoring prompt for this scenario. */
    rubricContext: text('rubric_context'),
    sortOrder: integer('sort_order').default(0).notNull(),
    /** RETIRED-STALE (W8, 2026-07-21) — when set, this scenario has been retired
     *  and is excluded from fit copy, scores, chips, and re-scoring. Nullable
     *  stamp, NEVER a DELETE (nothing-unrecoverable): a retired scenario is kept
     *  and reactivatable. Distinct from `active` (a user-facing pause) — this is
     *  "retired because its constraints contradict current profile truth" (e.g.
     *  a pre-correction Australia-move scenario on a US home base). Additive;
     *  see docs/design/scenario-location-truth-2026-07-21.md for the DDL. Serve-
     *  time DERIVED staleness (scenarioStaleness.ts) needs no column — this
     *  powers the durable, user-driven retire/reactivate. */
    retiredAt: timestamp('retired_at', { withTimezone: true }),
    /** Why the scenario was retired (structured/short) — surfaced in the honest
     *  "an outdated scenario was retired" state. Null when never retired. */
    retiredReason: text('retired_reason'),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('search_scenarios_user_idx').on(t.userId),
  }),
);
